import React, { useState, useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { toast, ToastContainer } from "react-toastify";
import "chart.js/auto";
import { Line } from "react-chartjs-2";
import AOS from "aos";
import "aos/dist/aos.css";
import BillModal from "./BillModal";
import Pagination from "./PaginationComp";
import api from "../utils/api";
import { formatIndianCurrency } from "../utils/Const";
import { useAuth } from "./authentication/AuthContext";
import {
  Search, Calendar, TrendingUp, Layers, Eye, RefreshCw, FileText,
  DollarSign, Trash2, AlertTriangle, CheckSquare, Square, CheckCircle2,
} from "lucide-react";

const OrderHistory = () => {
  // Deleting bills removes revenue from reports — owner/manager only (matches the server)
  const { isStaff, staffRole } = useAuth();
  const canDeleteBills = !isStaff || staffRole === "manager";
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("all");
  const [userId, setUserId] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isBillModalOpen, setIsBillModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [chartType, setChartType] = useState("daily");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("orders");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("all");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  // Single delete
  const [deleteModal, setDeleteModal] = useState({ open: false, bill: null, input: "", loading: false });

  // Multi-select
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Bulk delete modal
  const [bulkModal, setBulkModal] = useState({ open: false, loading: false });

  // Delete summary
  const [summary, setSummary] = useState(null); // { count, totalAmount, firstDate, lastDate }

  const PAYMENT_METHODS = [
    { value: "all",    label: "All",    color: "slate" },
    { value: "CASH",   label: "Cash",   color: "emerald" },
    { value: "UPI",    label: "UPI",    color: "violet" },
    { value: "CARD",   label: "Card",   color: "blue" },
    { value: "COD",    label: "COD",    color: "amber" },
    { value: "WALLET", label: "Wallet", color: "pink" },
    { value: "SPLIT",  label: "Split",  color: "orange" },
  ];

  const PAYMENT_COLORS = {
    CASH:   "bg-emerald-100 text-emerald-700",
    UPI:    "bg-violet-100 text-violet-700",
    CARD:   "bg-blue-100 text-blue-700",
    COD:    "bg-amber-100 text-amber-700",
    WALLET: "bg-pink-100 text-pink-700",
    SPLIT:  "bg-orange-100 text-orange-700",
  };

  const FILTER_PILLS = [
    { value: "today",     label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "thisweek",  label: "This Week" },
    { value: "thismonth", label: "This Month" },
    { value: "thisyear",  label: "This Year" },
    { value: "custom",    label: "📅 Custom" },
  ];

  const getBillDate = (item) => item.createdAt || item.timestamp || null;

  useEffect(() => { AOS.init({ duration: 800, once: true, mirror: false }); }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      if (decoded.user) setUserId(decoded.user.id);
    }
  }, []);

  useEffect(() => { if (userId) fetchData(); }, [userId, filter, currentPage, search, itemsPerPage, paymentMethod]);

  // Amount filters refetch once typing pauses (they were never applied before)
  const amountFiltersTouched = useRef(false);
  useEffect(() => {
    if (!amountFiltersTouched.current) {
      amountFiltersTouched.current = true; // initial render: the effect above already fetched
      return;
    }
    if (!userId) return;
    const t = setTimeout(fetchData, 500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minAmount, maxAmount]);

  // Clear selections when data changes
  useEffect(() => { setSelectedIds(new Set()); }, [data]);

  const fetchData = async () => {
    try {
      setLoading(true);
      let url = `/bills/history?page=${currentPage}&limit=${itemsPerPage}&search=${encodeURIComponent(search)}&filter=${filter}&paymentMethod=${paymentMethod}`;
      if (minAmount) url += `&minAmount=${minAmount}`;
      if (maxAmount) url += `&maxAmount=${maxAmount}`;
      if (filter === "custom" && fromDate && toDate) url += `&fromDate=${fromDate}&toDate=${toDate}`;
      const res = await api.get(url);
      const d = res.data;
      setData(d.data || []);
      setTotalPages(d.totalPages || 1);
      setTotalRevenue(d.totalRevenue || 0);
      setTotalOrders(d.totalOrders || 0);
      setChartData(d.chartData || []);
      setChartType(d.chartType || "daily");
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (value) => {
    const next = filter === value ? "all" : value;
    setFilter(next);
    setCurrentPage(1);
    setShowDatePicker(next === "custom");
    if (next !== "custom") { setFromDate(""); setToDate(""); }
  };

  const getChartData = () => ({
    labels: chartData.map((d) => d.label),
    datasets: [{
      label: chartType === "hourly" ? "Hourly Sales (₹)" : "Daily Sales (₹)",
      data: chartData.map((d) => d.total),
      borderColor: "rgba(13, 148, 136, 1)",
      backgroundColor: "rgba(13, 148, 136, 0.1)",
      fill: true, tension: 0.4, borderWidth: 3,
      pointBackgroundColor: "rgba(13, 148, 136, 1)", pointHoverRadius: 6,
    }],
  });

  const handleViewBill = (order) => { setSelectedOrder(order); setIsBillModalOpen(true); };

  // ── Single delete ──────────────────────────────────────────────────────────
  const handleSoftDelete = async () => {
    const bill = deleteModal.bill;
    if (!bill) return;
    setDeleteModal((d) => ({ ...d, loading: true }));
    try {
      await api.delete(`/bills/${bill._id}`);
      toast.success("Order removed from history");
      setDeleteModal({ open: false, bill: null, input: "", loading: false });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete order");
      setDeleteModal((d) => ({ ...d, loading: false }));
    }
  };

  // ── Multi-select helpers ───────────────────────────────────────────────────
  const allSelected = data.length > 0 && data.every((b) => selectedIds.has(b._id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.map((b) => b._id)));
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  // ── Bulk delete ────────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    setBulkModal((m) => ({ ...m, loading: true }));
    try {
      const res = await api.post("/bills/bulk-delete", { billIds: [...selectedIds] });
      setBulkModal({ open: false, loading: false });
      setSelectedIds(new Set());
      setSummary(res.data.summary);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Bulk delete failed");
      setBulkModal((m) => ({ ...m, loading: false }));
    }
  };

  const formatDate = (item) => {
    const date = getBillDate(item);
    return date
      ? new Date(date).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true })
      : "-";
  };

  const formatSummaryDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "—";

  // selected bills for summary display in bulk modal
  const selectedBillsData = data.filter((b) => selectedIds.has(b._id));
  const selectedTotal = selectedBillsData.reduce((sum, b) => sum + (b.totalAmount || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50/50 pt-20 pb-12 px-4 md:px-8">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200/60 pb-5">
          <div>
            <h1 className="text-3xl font-black text-slate-800 tracking-tight">Order History</h1>
            <p className="text-sm text-slate-500 font-medium mt-1">Browse, search, and analyze invoice history and revenue analytics.</p>
          </div>
          <button onClick={fetchData}
            className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-600 hover:text-slate-900 transition hover:bg-slate-50 hover:shadow-sm"
            title="Refresh Records">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white rounded-3xl p-6 border border-slate-200/65 shadow-sm hover:shadow-md transition duration-300 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition duration-300">
              <Layers className="w-24 h-24 text-teal-600" />
            </div>
            <span className="text-[10px] font-black uppercase text-teal-500 bg-teal-50 px-2.5 py-1 rounded-full w-fit">Total Invoices</span>
            <h3 className="text-4xl font-black text-slate-850 mt-4 tracking-tight">{totalOrders}</h3>
            <p className="text-xs text-slate-400 font-semibold mt-1 flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-500" /> Total billing count
            </p>
          </div>
          <div className="bg-white rounded-3xl p-6 border border-slate-200/65 shadow-sm hover:shadow-md transition duration-300 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition duration-300">
              <DollarSign className="w-24 h-24 text-indigo-650" />
            </div>
            <span className="text-[10px] font-black uppercase text-indigo-500 bg-indigo-50 px-2.5 py-1 rounded-full w-fit">Total Revenue</span>
            <h3 className="text-4xl font-black text-slate-850 mt-4 tracking-tight">₹{Math.round(totalRevenue || 0).toLocaleString("en-IN")}</h3>
            <p className="text-xs text-slate-400 font-semibold mt-1 flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-500" /> Net calculated earnings
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-3xl p-5 border border-slate-200/65 shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <p className="text-[10px] font-black uppercase text-slate-400 mb-1.5 tracking-widest">Search</p>
              <div className="relative">
                <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
                <input type="text" placeholder="Search name, mobile, billId..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none text-sm font-semibold transition" />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-black uppercase text-slate-400 mb-1.5 tracking-widest">Amount Range (₹)</p>
              <div className="flex gap-2 items-center">
                <input type="number" placeholder="Min" value={minAmount} min={0}
                  onChange={(e) => { setMinAmount(e.target.value); setCurrentPage(1); }}
                  className="flex-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-teal-500 focus:outline-none bg-slate-50/50" />
                <span className="text-slate-300 font-black">–</span>
                <input type="number" placeholder="Max" value={maxAmount} min={0}
                  onChange={(e) => { setMaxAmount(e.target.value); setCurrentPage(1); }}
                  className="flex-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-teal-500 focus:outline-none bg-slate-50/50" />
                {(minAmount || maxAmount) && (
                  <button onClick={() => { setMinAmount(""); setMaxAmount(""); setCurrentPage(1); }}
                    className="text-xs text-red-500 hover:text-red-700 font-bold px-2 shrink-0 transition">Clear</button>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 pt-3 border-t border-slate-100">
            <div className="flex-1">
              <p className="text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Date Range</p>
              <div className="flex flex-wrap gap-2">
                {FILTER_PILLS.map((pill) => (
                  <button key={pill.value} onClick={() => handleFilterChange(pill.value)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                      filter === pill.value
                        ? "bg-teal-600 text-white border-teal-600 shadow-sm shadow-teal-600/20"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-600"
                    }`}>
                    {pill.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Payment Method</p>
              <div className="flex flex-wrap gap-2">
                {PAYMENT_METHODS.map((pm) => (
                  <button key={pm.value} onClick={() => { setPaymentMethod(pm.value); setCurrentPage(1); }}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                      paymentMethod === pm.value
                        ? "bg-slate-800 text-white border-slate-800 shadow-sm"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-800"
                    }`}>
                    {pm.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Custom Date Range Picker */}
        {(showDatePicker || filter === "custom") && (
          <div className="bg-white border border-teal-200 p-5 rounded-3xl shadow-sm" data-aos="fade-down">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-4 h-4 text-teal-600" />
              <span className="text-sm font-black text-slate-700">Select Date Range</span>
              {fromDate && toDate && (
                <span className="ml-auto text-xs font-bold text-teal-600 bg-teal-50 px-2.5 py-1 rounded-lg">
                  {new Date(fromDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  {" → "}
                  {new Date(toDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex flex-col flex-1 w-full">
                <label className="text-xs font-bold text-slate-500 mb-1">From</label>
                <input type="date" value={fromDate} max={toDate || undefined}
                  onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
                  className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-teal-500 focus:outline-none bg-slate-50/50 w-full" />
              </div>
              <div className="flex items-center justify-center pb-2.5 text-slate-300 font-black text-lg hidden sm:flex">→</div>
              <div className="flex flex-col flex-1 w-full">
                <label className="text-xs font-bold text-slate-500 mb-1">To</label>
                <input type="date" value={toDate} min={fromDate || undefined}
                  onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
                  className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-teal-500 focus:outline-none bg-slate-50/50 w-full" />
              </div>
              <button onClick={() => { if (fromDate && toDate) fetchData(); }}
                disabled={!fromDate || !toDate}
                className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-extrabold text-sm rounded-xl shadow transition w-full sm:w-auto">
                Apply
              </button>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-1.5 flex gap-2 w-fit">
          {[["orders", "📋 Invoice Records"], ["graph", "📈 Graphical View"]].map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 text-xs font-black uppercase tracking-wider rounded-xl transition-all ${
                activeTab === tab ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}>
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center min-h-[35vh] bg-white rounded-3xl border border-slate-200/60 shadow-sm">
            <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Retrieving Records...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-12 text-center">
            <span className="text-4xl block mb-3">📭</span>
            <h3 className="text-lg font-bold text-slate-800">No records found</h3>
            <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">No orders match your selected filters.</p>
          </div>
        ) : (
          <div className="w-full">
            {activeTab === "graph" && (
              <div className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm">
                <div className="h-[40vh] min-h-[300px] w-full flex items-center justify-center">
                  <Line data={getChartData()} options={{ responsive: true, maintainAspectRatio: false }} />
                </div>
              </div>
            )}

            {activeTab === "orders" && (
              <div className="space-y-4">

                {/* Control row */}
                <div className="flex flex-wrap justify-between items-center gap-3">
                  <div className="flex items-center gap-2">
                    {someSelected && canDeleteBills && (
                      <button
                        onClick={() => setBulkModal({ open: true, loading: false })}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold shadow transition"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Selected ({selectedIds.size})
                      </button>
                    )}
                    {someSelected && (
                      <button onClick={() => setSelectedIds(new Set())}
                        className="px-3 py-2 text-xs font-bold text-slate-500 hover:text-slate-800 border border-slate-200 rounded-xl hover:bg-slate-50 transition">
                        Clear Selection
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <span>Show</span>
                    <select value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      className="bg-white border border-slate-200 rounded-lg px-2.5 py-1 font-bold text-slate-700 outline-none focus:ring-1 focus:ring-teal-500">
                      <option value={10}>10 entries</option>
                      <option value={25}>25 entries</option>
                      <option value={50}>50 entries</option>
                    </select>
                  </div>
                </div>

                {/* Desktop Table */}
                <div className="hidden md:block bg-white rounded-3xl border border-slate-200/70 shadow-sm overflow-x-auto">
                  <table className="min-w-[1050px] w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200/60">
                      <tr>
                        <th className="py-4 px-4 w-10">
                          <button onClick={toggleSelectAll} className="text-slate-400 hover:text-slate-700 transition">
                            {allSelected ? <CheckSquare className="w-4 h-4 text-teal-600" /> : <Square className="w-4 h-4" />}
                          </button>
                        </th>
                        <th className="py-4 px-5 text-left font-black text-slate-600 text-xs uppercase tracking-wider">Date</th>
                        <th className="py-4 px-5 text-left font-black text-slate-600 text-xs uppercase tracking-wider">Bill ID</th>
                        <th className="py-4 px-5 text-left font-black text-slate-600 text-xs uppercase tracking-wider">Customer</th>
                        <th className="py-4 px-5 text-left font-black text-slate-600 text-xs uppercase tracking-wider">Mobile</th>
                        <th className="py-4 px-5 text-left font-black text-slate-600 text-xs uppercase tracking-wider">Payment</th>
                        <th className="py-4 px-5 text-left font-black text-slate-600 text-xs uppercase tracking-wider">GST</th>
                        <th className="py-4 px-5 text-left font-black text-slate-600 text-xs uppercase tracking-wider">Discount</th>
                        <th className="py-4 px-5 text-left font-black text-slate-600 text-xs uppercase tracking-wider">Total</th>
                        <th className="py-4 px-5 text-center font-black text-slate-600 text-xs uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                      {data.map((bill) => (
                        <tr key={bill._id}
                          className={`hover:bg-slate-50/50 transition ${selectedIds.has(bill._id) ? "bg-teal-50/40" : ""}`}>
                          <td className="py-4 px-4">
                            <button onClick={() => toggleOne(bill._id)} className="text-slate-400 hover:text-teal-600 transition">
                              {selectedIds.has(bill._id)
                                ? <CheckSquare className="w-4 h-4 text-teal-600" />
                                : <Square className="w-4 h-4" />}
                            </button>
                          </td>
                          <td className="py-4 px-5 text-xs text-slate-500">{formatDate(bill)}</td>
                          <td className="py-4 px-5 font-mono text-slate-900 font-bold">{bill.billId || "NA"}</td>
                          <td className="py-4 px-5 truncate max-w-[150px]">{bill.customerName || "Walk-in"}</td>
                          <td className="py-4 px-5 font-mono text-sm">{bill.customerMobile || "—"}</td>
                          <td className="py-4 px-5">
                            <span className={`text-[11px] font-black px-2.5 py-1 rounded-lg ${PAYMENT_COLORS[bill.payment?.method] || "bg-slate-100 text-slate-600"}`}>
                              {bill.payment?.method || "—"}
                            </span>
                          </td>
                          <td className="py-4 px-5 text-slate-500">{((bill?.gstPercentage ?? 0) * 1).toFixed(2)}%</td>
                          <td className="py-4 px-5 text-slate-500">{((bill?.discountPercentage ?? 0) * 1).toFixed(2)}%</td>
                          <td className="py-4 px-5 font-bold text-slate-900 font-mono">₹{formatIndianCurrency(bill?.totalAmount)}</td>
                          <td className="py-4 px-5 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button onClick={() => handleViewBill(bill)}
                                className="p-2 border border-slate-200 hover:border-teal-500 rounded-xl hover:bg-teal-50 text-slate-500 hover:text-teal-600 transition"
                                title="View Bill Details">
                                <Eye className="w-4 h-4" />
                              </button>
                              {canDeleteBills && (
                              <button onClick={() => setDeleteModal({ open: true, bill, input: "", loading: false })}
                                className="p-2 border border-slate-200 hover:border-red-400 rounded-xl hover:bg-red-50 text-slate-400 hover:text-red-600 transition"
                                title="Remove from history">
                                <Trash2 className="w-4 h-4" />
                              </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden space-y-3">
                  {data.map((bill) => (
                    <div key={bill._id}
                      className={`bg-white p-4 rounded-2xl border shadow-sm flex items-center justify-between gap-4 transition ${
                        selectedIds.has(bill._id) ? "border-teal-300 bg-teal-50/40" : "border-slate-200"
                      }`}>
                      <button onClick={() => toggleOne(bill._id)} className="shrink-0 text-slate-400 hover:text-teal-600 transition">
                        {selectedIds.has(bill._id)
                          ? <CheckSquare className="w-5 h-5 text-teal-600" />
                          : <Square className="w-5 h-5" />}
                      </button>
                      <div className="space-y-1 flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-black font-mono text-slate-800">{bill.billId || "NA"}</span>
                          <span className="text-[10px] text-slate-400 font-bold">{formatDate(bill)}</span>
                          <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-md ${PAYMENT_COLORS[bill.payment?.method] || "bg-slate-100 text-slate-600"}`}>
                            {bill.payment?.method || "NA"}
                          </span>
                        </div>
                        <p className="text-sm font-extrabold text-slate-700 truncate">{bill.customerName || "Walk-In Customer"}</p>
                        <p className="text-xs font-mono text-slate-400">{bill.customerMobile || "No mobile"}</p>
                        <div className="flex flex-col mt-1.5">
                          <p className="text-[10px] text-slate-400 font-semibold mb-0.5">
                            GST: {((bill?.gstPercentage ?? 0) * 1).toFixed(2)}% • Disc: {((bill?.discountPercentage ?? 0) * 1).toFixed(2)}%
                          </p>
                          <p className="text-base font-black text-teal-600 font-mono">₹{formatIndianCurrency(bill?.totalAmount)}</p>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                        <button onClick={() => handleViewBill(bill)}
                          className="p-3 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-xl border border-teal-100 transition flex items-center justify-center">
                          <Eye className="w-4 h-4" />
                        </button>
                        {canDeleteBills && (
                        <button onClick={() => setDeleteModal({ open: true, bill, input: "", loading: false })}
                          className="p-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl border border-red-100 transition flex items-center justify-center">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="pt-4">
                  <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} />
                </div>

              </div>
            )}
          </div>
        )}

      </div>

      {/* Bill View Modal */}
      {isBillModalOpen && selectedOrder && (
        <BillModal billData={selectedOrder} closeModal={() => setIsBillModalOpen(false)} />
      )}

      {/* Single Delete Confirmation Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 border border-red-100">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-800">Remove Order from History?</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Bill <span className="font-bold font-mono">{deleteModal.bill?.billId}</span> will be soft-deleted.
                  Super Admin can still view and restore it.
                </p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
              <p className="text-xs font-black text-red-700 uppercase tracking-widest mb-2">⚠ Type to confirm</p>
              <p className="text-xs text-red-600 mb-3">
                Type <span className="font-black font-mono bg-red-100 px-1.5 py-0.5 rounded">deleteme</span> to proceed
              </p>
              <input type="text" value={deleteModal.input}
                onChange={(e) => setDeleteModal((d) => ({ ...d, input: e.target.value }))}
                placeholder="Type deleteme to confirm..."
                className="w-full px-3 py-2 border border-red-300 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-400 bg-white"
                autoFocus />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteModal({ open: false, bill: null, input: "", loading: false })}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-50 transition">
                Cancel
              </button>
              <button onClick={handleSoftDelete}
                disabled={deleteModal.input.trim().toLowerCase() !== "deleteme" || deleteModal.loading}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-extrabold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition">
                {deleteModal.loading ? "Removing..." : "Remove Order"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 border border-red-100">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-800">Delete {selectedIds.size} Selected Orders?</h2>
                <p className="text-sm text-slate-500 mt-1">These orders will be soft-deleted and can be restored by Super Admin.</p>
              </div>
            </div>

            {/* Mini summary of what will be deleted */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 mb-5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-slate-500">Orders selected</span>
                <span className="font-black text-slate-800">{selectedIds.size}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-slate-500">Total amount</span>
                <span className="font-black text-teal-700">₹{Math.round(selectedTotal).toLocaleString("en-IN")}</span>
              </div>
              <div className="border-t border-slate-200 pt-2 max-h-40 overflow-y-auto space-y-1">
                {selectedBillsData.map((b) => (
                  <div key={b._id} className="flex justify-between text-xs text-slate-500">
                    <span className="font-mono">{b.billId || "—"}</span>
                    <span className="font-semibold">₹{formatIndianCurrency(b.totalAmount)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setBulkModal({ open: false, loading: false })}
                disabled={bulkModal.loading}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-50 transition disabled:opacity-40">
                Cancel
              </button>
              <button onClick={handleBulkDelete} disabled={bulkModal.loading}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-extrabold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition">
                {bulkModal.loading ? "Deleting..." : `Delete ${selectedIds.size} Orders`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Summary Modal (shown after bulk delete) */}
      {summary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 border border-teal-100">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mb-3">
                <CheckCircle2 className="w-7 h-7 text-teal-600" />
              </div>
              <h2 className="text-xl font-black text-slate-800">Orders Deleted</h2>
              <p className="text-sm text-slate-500 mt-1">Successfully removed from history.</p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200">
                <p className="text-xs font-black uppercase text-slate-400 tracking-widest mb-1">Orders Deleted</p>
                <p className="text-3xl font-black text-slate-800">{summary.count}</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-4 text-center border border-teal-100">
                <p className="text-xs font-black uppercase text-teal-500 tracking-widest mb-1">Total Amount</p>
                <p className="text-2xl font-black text-teal-700">₹{Math.round(summary.totalAmount).toLocaleString("en-IN")}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200">
                <p className="text-xs font-black uppercase text-slate-400 tracking-widest mb-1">First Order</p>
                <p className="text-sm font-black text-slate-700">{formatSummaryDate(summary.firstDate)}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200">
                <p className="text-xs font-black uppercase text-slate-400 tracking-widest mb-1">Last Order</p>
                <p className="text-sm font-black text-slate-700">{formatSummaryDate(summary.lastDate)}</p>
              </div>
            </div>

            <button onClick={() => setSummary(null)}
              className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-extrabold rounded-xl transition">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistory;
